import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useId } from "react";
import { apiGet } from "../../lib/api";
import { COINS_QR, type CoinQr, type ReglageQr, reglageQrSchema } from "../contract";
import { detailDe, envoyer, issueIncertaine } from "../diffusion-api";
import { PANNEAU, TITRE_PANNEAU } from "./cadre";
import {
  COINS_LIBELLES,
  QR_ALLUME,
  QR_CHARGEMENT,
  QR_COIN_LEGENDE,
  QR_ECRITURE_ECHEC,
  QR_ECRITURE_INCERTAINE,
  QR_ECRITURE_SESSION,
  QR_ENREGISTRE,
  QR_ENREGISTREMENT,
  QR_ETEINT,
  QR_INTERRUPTEUR,
  QR_INTRO,
  QR_LECTURE_ECHEC,
  QR_NOTE_OBS,
  QR_TITRE,
  qrCoinChoisi,
} from "./reglages-texts";

/**
 * Le QR code du stream, sur la page « Overlays d'OBS » (`/regie/reglages/overlays`) : le coin de l'ecran ou
 * `/overlay/taverne` le pose, et un interrupteur. `GET` et `PUT /api/direct/qr` (backend `direct/routes-reglages.ts`).
 *
 * - **Le reglage vient du serveur.** Chaque geste envoie les deux champs d'un coup (`PUT`), et l'ecran montre ce que le
 *   serveur rend, jamais ce qu'il a demande : si l'ecriture echoue, l'etat affiche est relu, pas suppose. Les
 *   commandes sont **occupees** le temps d'une ecriture (`aria-disabled`, jamais `disabled` : Chromium retire le focus
 *   d'une commande qui devient desactivee, comme `BoutonOccupe`, `diffusion-commun.tsx`) : elles gardent le focus et un
 *   geste de plus est ignore, pour qu'une seconde ecriture ne parte pas avec l'etat d'avant la premiere ;
 * - **Il part eteint** (`reglages.ts` du backend) : rien n'apparait au stream tant que le geste n'est pas fait ;
 * - **Rien du serveur ne s'affiche** : un refus devient une phrase de `reglages-texts.ts`.
 * - **Le choix ne tient pas qu'a la couleur** : le coin choisi est un bouton radio natif (clavier, lecteur d'ecran), son
 *   remplissage s'accompagne d'une coche, et une phrase dit le coin et l'etat.
 */

const QR_KEY = ["direct", "qr"] as const;

/** La grille des coins : chaque chip se pose dans son coin du cadre, qui a la forme d'un ecran 16:9. */
const PLACE: Readonly<Record<CoinQr, string>> = {
  "haut-gauche": "col-start-1 row-start-1 justify-self-start self-start",
  "haut-droit": "col-start-2 row-start-1 justify-self-end self-start",
  "bas-gauche": "col-start-1 row-start-2 justify-self-start self-end",
  "bas-droit": "col-start-2 row-start-2 justify-self-end self-end",
};

/** Les touches qui font passer d'un bouton radio au suivant. */
const TOUCHES_FLECHES: ReadonlySet<string> = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
]);

type Message = { ton: "ok" | "erreur"; texte: string; alerte: boolean };

/** La phrase d'une ecriture refusee ou sans reponse. */
function messageEcritureEchec(erreur: unknown): Message {
  const { status } = detailDe(erreur);
  if (status === 401 || status === 403) {
    return { ton: "erreur", texte: QR_ECRITURE_SESSION, alerte: true };
  }
  return {
    ton: "erreur",
    texte: issueIncertaine(erreur) ? QR_ECRITURE_INCERTAINE : QR_ECRITURE_ECHEC,
    alerte: true,
  };
}

export function QrOverlay(): ReactElement {
  const queryClient = useQueryClient();
  const idTitre = useId();
  const idInterrupteur = useId();
  const nomGroupe = useId();

  const lecture = useQuery({
    queryKey: QR_KEY,
    queryFn: () => apiGet("/api/direct/qr", reglageQrSchema),
    retry: false,
    gcTime: 0,
  });

  const ecriture = useMutation({
    mutationFn: (suivant: ReglageQr) => envoyer("PUT", "/api/direct/qr", suivant, reglageQrSchema),
    gcTime: 0,
    // Ce que le serveur rend fait foi.
    onSuccess: (pose) => queryClient.setQueryData(QR_KEY, pose),
    // Un refus, ou une reponse qui n'est pas arrivee : l'ecriture a pu aboutir, l'ecran relit ce que le serveur garde.
    onError: () => void queryClient.invalidateQueries({ queryKey: QR_KEY }),
  });

  const reglage = lecture.data;
  const occupe = ecriture.isPending;

  function poser(suivant: ReglageQr): void {
    ecriture.reset();
    ecriture.mutate(suivant);
  }

  let message: Message | null = null;
  if (occupe) message = { ton: "ok", texte: QR_ENREGISTREMENT, alerte: false };
  else if (ecriture.isError) message = messageEcritureEchec(ecriture.error);
  else if (ecriture.isSuccess) message = { ton: "ok", texte: QR_ENREGISTRE, alerte: false };

  return (
    <section className={PANNEAU} aria-labelledby={idTitre}>
      <h2 id={idTitre} className={TITRE_PANNEAU}>
        {QR_TITRE}
      </h2>
      <p className="m-0 text-muted-foreground">{QR_INTRO}</p>

      {lecture.isPending ? (
        <p className="m-0 text-muted-foreground">{QR_CHARGEMENT}</p>
      ) : reglage === undefined ? (
        <p role="alert" className="m-0 text-destructive">
          {QR_LECTURE_ECHEC}
        </p>
      ) : (
        <>
          <label
            htmlFor={idInterrupteur}
            className="flex min-h-12 w-full max-w-[480px] cursor-pointer items-center gap-4 has-[[aria-disabled=true]]:cursor-wait"
          >
            <input
              id={idInterrupteur}
              type="checkbox"
              role="switch"
              aria-checked={reglage.visible}
              aria-disabled={occupe ? true : undefined}
              className="peer sr-only"
              checked={reglage.visible}
              onChange={(evenement) => {
                if (occupe) return;
                poser({ coin: reglage.coin, visible: evenement.currentTarget.checked });
              }}
            />
            <span
              aria-hidden="true"
              className="relative h-8 w-14 shrink-0 rounded-full border-2 border-input bg-[var(--nuit)] after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:rounded-full after:bg-input peer-checked:border-primary peer-checked:bg-primary peer-checked:after:left-7 peer-checked:after:bg-[var(--nuit)] peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background peer-aria-disabled:opacity-50"
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-foreground">{QR_INTERRUPTEUR}</span>
              {/* L'etat s'ecrit en toutes lettres sous le libelle ; un lecteur d'ecran l'entend par `aria-checked`. */}
              <span aria-hidden="true" className="police-nom text-[28px] text-[#FCDD8E]">
                {reglage.visible ? QR_ALLUME : QR_ETEINT}
              </span>
            </span>
          </label>

          <fieldset className="m-0 flex min-w-0 flex-col gap-3 border-0 p-0">
            <legend className="mb-3 p-0 text-foreground">{QR_COIN_LEGENDE}</legend>
            <div className="grid aspect-video w-full max-w-[360px] grid-cols-2 grid-rows-2 gap-2 rounded-[4px] border-2 border-dashed border-input bg-[var(--nuit)] p-2">
              {COINS_QR.map((coin) => (
                <label
                  key={coin}
                  className={`${PLACE[coin]} relative flex min-h-12 min-w-0 cursor-pointer items-center justify-center gap-2 rounded-[4px] border-2 border-input bg-card px-3 py-1 text-[20px] has-[:checked]:border-primary has-[:checked]:bg-primary has-[:checked]:text-primary-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background has-[[aria-disabled=true]]:cursor-wait has-[[aria-disabled=true]]:opacity-60`}
                >
                  <input
                    type="radio"
                    name={nomGroupe}
                    value={coin}
                    aria-disabled={occupe ? true : undefined}
                    className="peer sr-only"
                    checked={reglage.coin === coin}
                    onChange={() => {
                      if (occupe) return;
                      poser({ coin, visible: reglage.visible });
                    }}
                    // Une fleche ne deplace ni le focus ni la selection tant que l'ecriture dure : sinon le focus
                    // courrait devant une selection que le serveur n'a pas encore confirmee.
                    onKeyDown={(evenement) => {
                      if (occupe && TOUCHES_FLECHES.has(evenement.key)) evenement.preventDefault();
                    }}
                  />
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="hidden h-4 w-4 peer-checked:block"
                  >
                    <path d="M2 6.5l3 3 5-6" />
                  </svg>
                  {COINS_LIBELLES[coin]}
                </label>
              ))}
            </div>
          </fieldset>
          <p className="m-0 text-muted-foreground">{qrCoinChoisi(reglage.coin, reglage.visible)}</p>
        </>
      )}

      {message !== null ? (
        <p
          role={message.alerte ? "alert" : "status"}
          className={message.ton === "erreur" ? "m-0 text-destructive" : "m-0 text-[#7FD3A6]"}
        >
          {message.texte}
        </p>
      ) : null}
      <p className="m-0 text-[19px] text-muted-foreground">{QR_NOTE_OBS}</p>
    </section>
  );
}
